/**
 * WebAuthn / Security Key browser helpers (SPEC-43).
 *
 * Thin typed wrappers on `@simplewebauthn/browser`.
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";
import { ApiError } from "./api";

export { browserSupportsWebAuthn };

export async function executeWebAuthnRegistration(
  optionsJSON: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  return await startRegistration({ optionsJSON });
}

export async function executeWebAuthnAuthentication(
  optionsJSON: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  return await startAuthentication({ optionsJSON });
}

/**
 * Maps WebAuthn ceremony and API errors to user-facing copy per SPEC-43 §2.3 / Unit #28:
 * - Registration vs Login context:
 *   - ApiError 401 / "unauthorized":
 *     - "registration" context (default) → "Registration session expired — please try registering again."
 *     - "login" context → "Login session expired — please sign in again."
 *   - ApiError "validation" + fieldErrors → first field error
 *   - ApiError with message → err.message
 * - NotAllowedError → "Security key required — plug in and tap your YubiKey." (+ Linux platform hint if on Linux)
 * - SecurityError → "Security key blocked — try another key or contact your admin."
 * - default → "Security key error — [message]" or "Security key error — try again."
 */
export function mapWebAuthnError(
  err: unknown,
  options?: { platform?: string; context?: "registration" | "login" },
): string {
  const context = options?.context ?? "registration";
  const platform = options?.platform;

  // 1. ApiError branch first
  if (err instanceof ApiError) {
    if (err.status === 401 || err.code === "unauthorized") {
      return context === "login"
        ? "Login session expired — please sign in again."
        : "Registration session expired — please try registering again.";
    }
    const feValues = Object.values(err.fieldErrors);
    if (feValues.length > 0 && typeof feValues[0] === "string" && feValues[0].length > 0) {
      return feValues[0];
    }
    if (typeof err.message === "string" && err.message.length > 0) {
      return err.message;
    }
  }

  // 2. DOMException / WebAuthn client error names
  if (err && typeof err === "object" && "name" in err) {
    const errorName = (err as { name: string }).name;
    if (errorName === "NotAllowedError") {
      const currentPlatform =
        platform ??
        (typeof navigator !== "undefined" && typeof navigator.platform === "string"
          ? navigator.platform
          : "");
      const isLinux = currentPlatform.toLowerCase().startsWith("linux");
      const linuxHint = isLinux
        ? " (Linux: check that pcscd is running and the YubiKey udev rules are installed.)"
        : "";
      return `Security key required — plug in and tap your YubiKey.${linuxHint}`;
    }
    if (errorName === "SecurityError") {
      return "Security key blocked — try another key or contact your admin.";
    }
  }

  // 3. General Error with message
  if (err instanceof Error && err.message) {
    return `Security key error — ${err.message}`;
  }

  // 4. Default fallback
  return "Security key error — try again.";
}
